import { useState } from "react";
import { useAuth } from "../auth/AuthContext";
import { isAdminHost, isClientHost } from "../auth/portal";
import { Navigate, useLocation } from "react-router-dom";
import { Instagram, Loader2, Lock, Mail, Shield } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";

export default function Login() {
  const { user, login } = useAuth();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (user) {
    const dest = user.role === "super_admin" ? "/admin" : "/";
    return <Navigate to={loc.state?.from || dest} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const u = await login(email.trim(), password);
      toast.success(`Welcome, ${u.name || u.email}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  const portalLabel = isAdminHost ? "Admin Console" : isClientHost ? "Client Portal" : null;

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-slate-900 flex items-center justify-center px-6 relative overflow-hidden">
      {/* Ambient signature glow */}
      <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[720px] h-[420px] rounded-full opacity-60"
           style={{ background: "radial-gradient(closest-side, rgba(229,99,167,0.14), transparent)" }} />
      <div className="w-full max-w-md relative">
        <div className="flex items-center gap-3 mb-8">
          <div className="p-2 rounded-xl bg-slate-900">
            {isAdminHost ? (
              <Shield size={20} className="text-white" />
            ) : (
              <Instagram size={20} className="text-white" />
            )}
          </div>
          <div className="flex items-baseline gap-1">
            <span className="text-2xl font-extrabold tracking-tight text-slate-900">CultureX</span>
            <span className="text-xs font-mono uppercase tracking-widest text-slate-400 ml-1 self-end mb-1.5">Intel</span>
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-[0_8px_30px_rgba(15,23,42,0.06)]">
          {portalLabel && (
            <div className="text-[10px] uppercase font-mono tracking-widest text-pink-600 mb-2"
                 data-testid="login-portal-label">
              {portalLabel}
            </div>
          )}
          <h1 className="text-lg font-semibold text-slate-900 mb-1">Sign in</h1>
          <p className="text-xs text-slate-400 font-mono tracking-wider mb-6">
            {isAdminHost
              ? "SUPER-ADMINS ONLY"
              : isClientHost
              ? "CLIENT WORKSPACE ACCESS"
              : "USE YOUR WORKSPACE CREDENTIALS"}
          </p>
          <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
            <div>
              <label className="text-[10px] uppercase font-mono tracking-widest text-slate-400 mb-1.5 block">
                Email
              </label>
              <div className="relative">
                <Mail size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  type="email"
                  value={email}
                  autoFocus
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-slate-50 border-slate-200 pl-9 font-mono text-xs text-slate-900"
                  data-testid="login-email-input"
                  placeholder="you@company.com"
                />
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase font-mono tracking-widest text-slate-400 mb-1.5 block">
                Password
              </label>
              <div className="relative">
                <Lock size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-slate-50 border-slate-200 pl-9 font-mono text-xs text-slate-900"
                  data-testid="login-password-input"
                  placeholder="••••••••"
                />
              </div>
            </div>
            <Button
              type="submit"
              disabled={submitting || !email || !password}
              className="w-full bg-slate-900 hover:bg-slate-800 text-white font-semibold h-10 mt-2 rounded-xl disabled:opacity-40"
              data-testid="login-submit"
            >
              {submitting ? (
                <><Loader2 size={13} className="mr-1.5 animate-spin" /> Signing in…</>
              ) : "Sign in"}
            </Button>
          </form>
        </div>
        <p className="text-[10px] font-mono text-slate-400 mt-6 text-center tracking-widest">
          NEED A WORKSPACE? CONTACT YOUR ADMIN
        </p>
      </div>
    </div>
  );
}
